import React, { useState } from 'react';
import { theme } from 'antd';
import { IconEngine } from '@tabler/icons-react';
import { SURFACE_1_SOFT } from '../theme/creamyTokens';

export interface IMachineryThumbnailProps {
  /** The machine's cover photo. Absent is ordinary, not an error -- see the fallback below. */
  url?: string;
  /** Square side, in px. */
  size?: number;
  alt?: string;
  style?: React.CSSProperties;
}

/**
 * A machine's cover photo at list size, with somewhere to land when there
 * isn't one.
 *
 * There are three ways to end up with no picture and they all look the
 * same to whoever is reading the screen: the booking has no machine at all
 * (a standalone service), the host never uploaded a photo, or the URL is
 * there but fails to load. None of them is a fault worth reporting, so all
 * three fall back to the same icon on a soft surface rather than a broken
 * image, an empty box, or a hole that makes the row jump once the request
 * fails.
 *
 * The rounded square is deliberate over a circle: machinery photos are
 * wide, and a circle crops an excavator down to a bucket.
 */
const MachineryThumbnail: React.FC<IMachineryThumbnailProps> = ({ url, size = 56, alt = '', style }) => {
  const { token } = theme.useToken();
  const [failed, setFailed] = useState(false);

  const shared: React.CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: Math.round(size * 0.28),
    // Same hairline the card itself carries, so the photo reads as part of
    // the card rather than pasted on top of it.
    border: `1px solid ${token.colorBorderSecondary}`,
    ...style,
  };

  if (!url || failed) {
    return (
      <div
        style={{
          ...shared,
          background: SURFACE_1_SOFT,
          color: token.colorTextTertiary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden="true"
      >
        <IconEngine size={Math.round(size * 0.45)} />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ ...shared, objectFit: 'cover', display: 'block', background: SURFACE_1_SOFT }}
    />
  );
};

export default MachineryThumbnail;
